import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CrcStatusType } from '@app/enums/CrcStatusType';
import {
    GetAllSitesResponse,
    GetPreRegisteredSitesResponse,
    GetSiteContactsResponse,
    GetSitePatientsResponse,
    GetStudySitesResponse,
    SendSiteRegistrationReminderResponse,
    SetPrimaryCrcRequest,
    SiteControllerv2Service,
    UpdateSiteInformationRequest,
} from '@app/generated-api';
import { createLazyLoadedState } from '@app/v2/utility';
import { getSiteBaseUrl } from '@utility/utility';
import { map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SiteService {
    private http = inject(HttpClient);
    generatedSiteService = inject(SiteControllerv2Service);

    getPreRegisteredSites(): Observable<GetPreRegisteredSitesResponse[]> {
        return this.http.get<GetPreRegisteredSitesResponse[]>(`${getSiteBaseUrl()}/api/v2/sites/preregistered`);
    }

    sendSiteRegistrationReminder: (siteId: number) => Observable<SendSiteRegistrationReminderResponse> = (
        siteId: number
    ) => {
        return this.http.post<SendSiteRegistrationReminderResponse>(
            `${getSiteBaseUrl()}/api/v2/sites/${siteId}/reminder`,
            null
        );
    };

    readonly numberOfSitesInStatus = createLazyLoadedState(() => ({
        fetch$: (statusId: number) =>
            this.http.get<number>(`${getSiteBaseUrl()}/api/v2/sites/status/${statusId}/count`),
    }));

    getStudySites: (studyId: number) => Observable<GetStudySitesResponse[]> = (studyId: number) => {
        return this.http.get<GetStudySitesResponse[]>(`${getSiteBaseUrl()}/api/v2/studies/${studyId}/sites`);
    };

    readonly allSites = createLazyLoadedState(() => ({
        fetch$: () => this.http.get<GetAllSitesResponse[]>(`${getSiteBaseUrl()}/api/v2/sites/list`),
    }));

    getAllSites: (statusIds: number[]) => Observable<GetAllSitesResponse[]> = (statusIds: number[]) => {
        return this.http.get<GetAllSitesResponse[]>(`${getSiteBaseUrl()}/api/v2/sites/list`, {
            params: new HttpParams({ fromObject: { statusIds } }),
        });
    };

    clearAllSites() {
        this.allSites.clear();
    }

    readonly siteOverview = createLazyLoadedState(() => ({
        fetch$: (siteId: number) => this.generatedSiteService.siteControllerv2GetSiteOverview(siteId),
    }));

    readonly siteContacts = createLazyLoadedState(() => ({
        fetch$: (siteId: number) =>
            this.http.get<GetSiteContactsResponse[]>(`${getSiteBaseUrl()}/api/v2/sites/${siteId}/contacts`).pipe(
                map((contacts) =>
                    contacts?.sort((a, b) => {
                        if (a.isPrimary) return -1;
                        else if (a.status === CrcStatusType.Active && !b.isPrimary) return -1;
                        else return 1;
                    })
                )
            ),
    }));

    setPrimaryCrc: (request: SetPrimaryCrcRequest) => Observable<void> = (request: SetPrimaryCrcRequest) => {
        return this.http.post<void>(`${getSiteBaseUrl()}/api/v2/sites/set-primary-crc`, request);
    };

    getSitePatients: (siteId: number) => Observable<GetSitePatientsResponse[]> = (siteId: number) => {
        return this.http.get<GetSitePatientsResponse[]>(`${getSiteBaseUrl()}/api/v2/sites/${siteId}/patients`);
    };

    updateSiteInfo: (request: UpdateSiteInformationRequest) => Observable<void> = (
        request: UpdateSiteInformationRequest
    ) => {
        return this.http.post<void>(`${getSiteBaseUrl()}/api/v2/sites/update/info`, request);
    };

    isPayQuickerCountrySupported(countryName: string): Observable<boolean> {
        return this.http.get<boolean>(
            `${getSiteBaseUrl()}/api/v2/sites/payquicker/country-supported?countryName=${encodeURIComponent(countryName)}`
        );
    }
}
